import appConfig from '../../config/appConfig';
import * as errorPageTypes from '../../consts/app/errorPageTypes';
import * as scpMethods from '../../consts/scp/scpMethods';
import {setMwDeviceInternalId} from '../../state/ducks/mwDevice';
import {selectIsFirmwareInProgress} from '../../state/selectors/iotDevice';
import {selectMwDeviceInternalId, selectMwDeviceIsIdentified} from '../../state/selectors/mwDevice';
import {dispatch, getState} from '../../state/store';
import urlUtils from '../../utils/urlUtils';
import appInstanceService from '../app/appInstanceService';
import backendService from '../app/backendService';
import authDataService from '../auth/authDataService';
import * as gamGigyaService from '../gam/gamGigyaService';
import consoleLog from '../logger/consoleLogger';
import log from '../logger/log';
import {validateTokenMapping} from '../mapping/scpMappings';
import appRouterService from '../route/appRouterService';
import server from '../server/server';
import cloudWatchProvider from './cloudWatchProvider';

let credentials = null;
let isAwsCredentialsRequested = false;
let currentAwsCredentialsRequest = null;
const FAILED_FETCH_TRIES_COUNT = 3;
let failedFetchCount = 0;

const isFetchAllowed = () => failedFetchCount < FAILED_FETCH_TRIES_COUNT;

const getCredentials = async (fetchNew) => {
    if (currentAwsCredentialsRequest) {
        await currentAwsCredentialsRequest;
    } else if (fetchNew || !credentials) {
        await fetchAwsCredentials();
    }

    return credentials;
};

const getRequestConfig = async () => {
    const state = getState();
    const authToken = await authDataService.getAuthToken();
    const appInstanceId = appInstanceService.getAppInstanceId();
    const isIdentified = selectMwDeviceIsIdentified(state);
    const deviceSerialNumber = isIdentified ? selectMwDeviceInternalId(state) : undefined;
    const isGamBackend = backendService.isGamBackend();
    const {codentify} = {};

    const data = {
        app_id: appConfig.getIccApiHeader_a(),
        app_instance_id: appInstanceId,
        cloudwatch_logging: appConfig.getIsAwsLoggingEnabled(),
        dusn: deviceSerialNumber,
        market_iso: appConfig.getScpCloudTopicMarket(),
        market_iso_prefix: appConfig.getScpCloudTopicMarketPrefix(),
        codentify,
        version: appConfig.getScpCloudVersion(),
    };

    const getHeaders = () => {
        let headers;

        if (isGamBackend) {
            const gigyaToken = gamGigyaService.getGamGigyaToken();
            // eslint-disable-next-line no-undef
            const gigyaApiKey = gigya.apiKey;

            headers = {
                Token: gigyaToken,
                'Api-Key': gigyaApiKey,
            };
        } else {
            headers = {Token: authToken};
        }
        return headers;
    };

    const headers = getHeaders();

    return {data, headers};
};

const getScpApiUrl = () => {
    const isGAMBackend = backendService.isGamBackend();

    return urlUtils.join(
        appConfig.getScpApiUrl(),
        isGAMBackend ? scpMethods.VALIDATE_SCP_TOKEN_V2 : scpMethods.VALIDATE_SCP_TOKEN
    );
};

const fetchAwsCredentials = async () => {
    const fetchIsNotAllowed = !isFetchAllowed();

    if (fetchIsNotAllowed) return;

    credentials = null;
    isAwsCredentialsRequested = true;
    const requestConfig = await getRequestConfig();
    const scpApiUrl = getScpApiUrl();

    try {
        currentAwsCredentialsRequest = server.post(scpApiUrl, requestConfig);
        const response = await currentAwsCredentialsRequest;

        currentAwsCredentialsRequest = null;
        isAwsCredentialsRequested = false;
        failedFetchCount = 0;

        log.debug(`fetchAwsCredentials: fetchAwsCredentials has successful response`);

        return onResponse(response);
    } catch (e) {
        if (e?.response?.status) {
            failedFetchCount = failedFetchCount + 1;
        }

        currentAwsCredentialsRequest = null;
        isAwsCredentialsRequested = false;

        consoleLog.info(`awsAuthenticationService: fetchAwsCredentials, error: ${e}`);

        return onError(e);
    }
};

const setInternalId = ({dusn}) => {
    const isIdentified = selectMwDeviceIsIdentified(getState());

    dispatch(setMwDeviceInternalId({deviceSerialNumber: dusn, isIdentified}));
};

const onResponse = (response) => {
    const mappedData = validateTokenMapping(response.data);

    setInternalId(mappedData);
    updateCredentials(mappedData);

    return mappedData;
};

const onError = async (e) => {
    if (!e?.response?.status) return;

    if (isAwsCredentialsRequested) return;

    const isFirmwareInProgress = selectIsFirmwareInProgress(getState());

    if (isFirmwareInProgress) return;

    appRouterService.forwardToErrorHandlingPage(errorPageTypes.ICC_TEMPORARY_ERROR);
    throw e;
};

const updateCredentials = (data) => {
    credentials = data;

    cloudWatchProvider.updateCloudWatchClient(credentials);
};

export default {
    getCredentials,
};
